var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __spreadArrays = (this && this.__spreadArrays) || function () {
    for (var s = 0, i = 0, il = arguments.length; i < il; i++) s += arguments[i].length;
    for (var r = Array(s), k = 0, i = 0; i < il; i++)
        for (var a = arguments[i], j = 0, jl = a.length; j < jl; j++, k++)
            r[k] = a[j];
    return r;
};
import { useState, useCallback, useContext, useRef, useEffect } from "react";
import axios from "axios";
import { createRequestError, } from "./request";
import { RequestContext } from "./requestContext";
import { useMountedState } from "./utils";
var REQUEST_AXIOS_INSTANCE_MESSAGE = "react-request-hook requires an Axios instance to be passed through context via the <RequestProvider>";
export function useRequest(fn) {
    var getMountedState = useMountedState();
    var axiosInstance = useContext(RequestContext);
    if (!axiosInstance) {
        throw new Error(REQUEST_AXIOS_INSTANCE_MESSAGE);
    }
    var _a = useState([]), sources = _a[0], setSources = _a[1];
    var hasPending = sources.length > 0;
    var removeCancelToken = useCallback(function (cancelToken) {
        if (getMountedState()) {
            setSources(function (prevSources) {
                return prevSources.filter(function (source) { return source.token !== cancelToken; });
            });
        }
    }, [getMountedState]);
    var callFn = useRef(fn);
    useEffect(function () {
        callFn.current = fn;
    }, [fn]);
    var request = useCallback(function () {
        var args = [];
        for (var _i = 0; _i < arguments.length; _i++) {
            args[_i] = arguments[_i];
        }
        var config = callFn.current.apply(callFn, args);
        var source = axios.CancelToken.source();
        var ready = function () {
            if (getMountedState()) {
                setSources(function (prevSources) { return __spreadArrays(prevSources, [source]); });
            }
            return axiosInstance(__assign(__assign({}, config), { cancelToken: source.token }))
                .then(function (response) {
                removeCancelToken(source.token);
                var data = response.data, restResponse = __rest(response, ["data"]);
                return [data, restResponse];
            })
                .catch(function (error) {
                removeCancelToken(source.token);
                throw createRequestError(error);
            });
        };
        return {
            ready: ready,
            cancel: source.cancel,
        };
    }, [axiosInstance, getMountedState, removeCancelToken]);
    var clear = useCallback(function (message) {
        if (sources.length > 0) {
            sources.map(function (source) { return source.cancel(message); });
            if (getMountedState()) {
                setSources([]);
            }
        }
    }, [getMountedState, sources]);
    var clearRef = useRef(clear);
    useEffect(function () {
        clearRef.current = clear;
    });
    var rtnClearFn = useCallback(function (message) { return clearRef.current(message); }, []);
    useEffect(function () {
        return clearRef.current;
    }, []);
    return [request, { clear: rtnClearFn, hasPending: hasPending }];
}
